const DEFAULT_API_PATH = '/api';
const DEFAULT_WS_PATH = '/ws';

type UrlType = 'api' | 'ws';

const hasScheme = (value: string): boolean => /^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//.test(value);
const isLocalhostHost = (host: string): boolean => host === 'localhost' || host === '127.0.0.1';
const isLocalDevServerPort = (port: string): boolean => port === '3000' || port === '8080';

const isPrivateNetworkHost = (host: string): boolean => {
  if (isLocalhostHost(host)) return true;
  const parts = host.split('.');
  if (parts.length !== 4 || parts.some(p => !/^\d+$/.test(p))) return false;
  const octets = parts.map(Number);
  // 10.0.0.0/8
  if (octets[0] === 10) return true;
  // 172.16.0.0/12
  if (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) return true;
  // 192.168.0.0/16
  if (octets[0] === 192 && octets[1] === 168) return true;
  return false;
};

const getWindowLocation = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location;
  }
  return undefined;
};

const shouldUseSameOriginLocalProxy = (): boolean => {
  const location = getWindowLocation();
  if (!location || !isPrivateNetworkHost(location.hostname)) {
    return false;
  }

  return location.port === '' || location.port === '80' || location.port === '443';
};

const getDefaultProtocol = (type: UrlType): string => {
  const location = getWindowLocation();
  if (location) {
    if (shouldUseSameOriginLocalProxy()) {
      if (type === 'api') {
        return location.protocol || 'http:';
      }
      return location.protocol === 'https:' ? 'wss:' : 'ws:';
    }

    const isSecureLocalhost =
      isLocalhostHost(location.hostname) &&
      isLocalDevServerPort(location.port) &&
      location.protocol === 'https:';
    if (type === 'api') {
      if (isSecureLocalhost) {
        return 'http:';
      }
      return location.protocol || 'http:';
    }
    if (isSecureLocalhost) {
      return 'ws:';
    }
    return location.protocol === 'https:' ? 'wss:' : 'ws:';
  }
  return type === 'api' ? 'http:' : 'ws:';
};

const getDefaultHost = (): string => {
  const location = getWindowLocation();
  return location?.hostname || 'localhost';
};

const normalizePort = (port: string | undefined, protocol: string, type: UrlType): string => {
  if (port && port !== '') {
    return port;
  }

  if (shouldUseSameOriginLocalProxy()) {
    if (protocol === 'https:' || protocol === 'wss:') {
      return '443';
    }
    return '80';
  }

  if (protocol === 'https:' || protocol === 'wss:') {
    return '443';
  }

  if (protocol === 'http:' || protocol === 'ws:') {
    return '8000';
  }

  if (type === 'api') {
    return '8000';
  }

  return '8000';
};

const getDefaultPort = (type: UrlType, protocol: string): string => {
  const location = getWindowLocation();
  if (shouldUseSameOriginLocalProxy()) {
    if (location?.port) {
      return location.port;
    }
    return protocol === 'https:' || protocol === 'wss:' ? '443' : '80';
  }

  if (location?.port) {
    if (isLocalDevServerPort(location.port)) {
      return '8000';
    }
    return location.port;
  }

  return normalizePort(undefined, protocol, type);
};

const buildDefaultBase = (type: UrlType): string => {
  const protocol = getDefaultProtocol(type);
  const host = getDefaultHost();
  const port = getDefaultPort(type, protocol);
  const path = type === 'api' ? DEFAULT_API_PATH : DEFAULT_WS_PATH;
  const omitPort =
    (protocol === 'https:' || protocol === 'wss:') && port === '443'
      ? true
      : (protocol === 'http:' || protocol === 'ws:') && port === '80';
  const portSegment = omitPort ? '' : `:${port}`;
  return `${protocol}//${host}${portSegment}${path}`;
};

const ensureProtocol = (raw: string, type: UrlType): string => {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return trimmed;
  }

  if (hasScheme(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('//')) {
    return `${getDefaultProtocol(type)}${trimmed}`;
  }

  if (trimmed.startsWith(':')) {
    return `${getDefaultProtocol(type)}//${getDefaultHost()}${trimmed}`;
  }

  if (trimmed.startsWith('/')) {
    const protocol = getDefaultProtocol(type);
    const host = getDefaultHost();
    const port = getDefaultPort(type, protocol);
    const omitPort =
      (protocol === 'https:' || protocol === 'wss:') && port === '443'
        ? true
        : (protocol === 'http:' || protocol === 'ws:') && port === '80';
    const portSegment = omitPort ? '' : `:${port}`;
    return `${protocol}//${host}${portSegment}${trimmed}`;
  }

  return `${getDefaultProtocol(type)}//${trimmed}`;
};

const normalizeBaseUrl = (raw: string | undefined, type: UrlType): string => {
  const fallback = buildDefaultBase(type);
  if (!raw) {
    return fallback;
  }

  const candidateWithScheme = ensureProtocol(raw, type);
  if (!candidateWithScheme) {
    return fallback;
  }

  const baseForRelative = (() => {
    const location = getWindowLocation();
    if (location?.origin) {
      return location.origin;
    }
    return 'http://localhost:3000';
  })();

  try {
    const url = new URL(candidateWithScheme, baseForRelative);

    if (!url.hostname) {
      url.hostname = getDefaultHost();
    }

    const targetProtocol =
      type === 'api'
        ? url.protocol || getDefaultProtocol(type)
        : url.protocol === 'https:' || url.protocol === 'wss:'
          ? 'wss:'
          : 'ws:';

    url.protocol = targetProtocol;

    const port = normalizePort(url.port, targetProtocol, type);
    const omitPort =
      (targetProtocol === 'https:' || targetProtocol === 'wss:') && port === '443'
        ? true
        : (targetProtocol === 'http:' || targetProtocol === 'ws:') && port === '80';
    url.port = omitPort ? '' : port;

    if (!url.pathname || url.pathname === '') {
      url.pathname = type === 'api' ? DEFAULT_API_PATH : DEFAULT_WS_PATH;
    }

    const formatted = url.toString().replace(/\/$/, '');
    return formatted;
  } catch (error) {
    console.warn(
      `[urlConfig] Invalid ${type} base URL '${raw}', falling back to ${fallback}`,
      error
    );
    return fallback;
  }
};

let cachedApiBase: string | null = null;
let cachedWsBase: string | null = null;
let wsUrlCheckLogged = false;

const shouldPreferSameOriginLocalProxy = (raw: string | undefined, type: UrlType): boolean => {
  const location = getWindowLocation();
  if (!location || !shouldUseSameOriginLocalProxy()) {
    return false;
  }
  if (!raw || raw.trim() === '') {
    return true;
  }

  try {
    const resolved = new URL(ensureProtocol(raw, type), location.origin);
    return isPrivateNetworkHost(resolved.hostname) && resolved.port === '8000';
  } catch {
    return false;
  }
};

const shouldForceLocalBase = (raw: string | undefined, type: UrlType): boolean => {
  const location = getWindowLocation();
  if (!location || !isPrivateNetworkHost(location.hostname)) {
    return false;
  }
  if (!raw || raw.trim() === '') {
    return false;
  }

  try {
    const resolved = new URL(ensureProtocol(raw, type));
    // Running UI from a LAN/localhost but pointing API/WS to non-local host (e.g. aimepic.com)
    // causes CORS and false "backend offline" signals in local prod-local runs.
    return !isPrivateNetworkHost(resolved.hostname);
  } catch {
    return false;
  }
};

/**
 * Dev-only: log once if WebSocket base URL looks wrong (missing /ws or duplicate /ws/ws).
 * Production builds should set REACT_APP_WS_URL to e.g. wss://aimepic.com/ws
 */
function checkWebSocketBaseUrlOnce(wsBase: string): void {
  if (wsUrlCheckLogged || typeof window === 'undefined') return;
  const isDev =
    isPrivateNetworkHost(window.location?.hostname || '') ||
    window.location?.port === '3000' ||
    process.env.NODE_ENV === 'development';
  if (!isDev) return;
  wsUrlCheckLogged = true;
  if (wsBase.includes('/ws/ws')) {
    console.warn(
      '[urlConfig] WebSocket base URL contains duplicate /ws/ws. Set REACT_APP_WS_URL to e.g. wss://host/ws (no trailing path).',
      { wsBase }
    );
  } else if (!wsBase.includes('/ws')) {
    console.warn(
      '[urlConfig] WebSocket base URL should contain /ws. Set REACT_APP_WS_URL for production (e.g. wss://aimepic.com/ws).',
      { wsBase }
    );
  }
}

export const getApiBaseUrl = (): string => {
  if (cachedApiBase) {
    return cachedApiBase;
  }
  if (shouldPreferSameOriginLocalProxy(process.env.REACT_APP_API_URL, 'api')) {
    cachedApiBase = buildDefaultBase('api');
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[urlConfig] Running on localhost behind nginx. Using same-origin API base:', cachedApiBase);
    }
    return cachedApiBase;
  }
  if (shouldForceLocalBase(process.env.REACT_APP_API_URL, 'api')) {
    cachedApiBase = buildDefaultBase('api');
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[urlConfig] Running on localhost with non-local REACT_APP_API_URL. Using local API base:', cachedApiBase);
    }
    return cachedApiBase;
  }
  cachedApiBase = normalizeBaseUrl(process.env.REACT_APP_API_URL, 'api');
  return cachedApiBase;
};

export const getWebsocketBaseUrl = (): string => {
  if (cachedWsBase) {
    return cachedWsBase;
  }
  if (shouldPreferSameOriginLocalProxy(process.env.REACT_APP_WS_URL, 'ws')) {
    cachedWsBase = buildDefaultBase('ws');
    checkWebSocketBaseUrlOnce(cachedWsBase);
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[urlConfig] Running on localhost behind nginx. Using same-origin WebSocket base:', cachedWsBase);
    }
    return cachedWsBase;
  }
  if (shouldForceLocalBase(process.env.REACT_APP_WS_URL, 'ws')) {
    cachedWsBase = buildDefaultBase('ws');
    checkWebSocketBaseUrlOnce(cachedWsBase);
    if (process.env.REACT_APP_DEBUG_VERBOSE_API === 'true') {
      console.log('[urlConfig] Running on localhost with non-local REACT_APP_WS_URL. Using local WebSocket base:', cachedWsBase);
    }
    return cachedWsBase;
  }
  cachedWsBase = normalizeBaseUrl(process.env.REACT_APP_WS_URL, 'ws');
  checkWebSocketBaseUrlOnce(cachedWsBase);
  return cachedWsBase;
};

export const resetUrlConfigCache = () => {
  cachedApiBase = null;
  cachedWsBase = null;
};

